<template>
  <b-card>
    <b-tabs
      v-model="activeTab"
      nav-wrapper-class="flex-nowrap overflow-auto"
      @input="changeTab"
    >
      <!-- Tab 1: ตั้งค่าความเสี่ยง (per Huay, BetType as columns) -->
      <b-tab lazy>
        <template #title>
          <span class="d-none d-sm-inline">{{ $t('ตั้งค่าความเสี่ยง') }}</span>
          <span class="d-sm-none">{{ $t('ความเสี่ยง') }}</span>
        </template>

        <!-- คำอธิบาย -->
        <b-alert
          variant="primary"
          show
          class="mb-2"
        >
          <div class="alert-body">
            {{ $t('กรุณาใส่จำนวนเงินที่ท่านต้องการถือสู้ ต่อตัว ของแต่ละประเภทหวยในช่องด้านล่าง เพื่อเปิดรับของ หากไม่ต้องการถือสู้ในประเภทใด ๆ ให้ใส่ 0') }}
          </div>
        </b-alert>

        <!-- แต่ละกลุ่มหวย -->
        <div
          v-for="group in huayGroups"
          :key="group.key"
          class="mb-4"
        >
          <h5 class="mb-2 group-title">
            {{ group.name }}
          </h5>

          <div class="table-responsive">
            <table class="table table-bordered table-sm risk-cap-table mb-2">
              <thead class="bg-primary">
                <tr>
                  <th style="min-width: 140px">
                    {{ $t('ชนิด') }}
                  </th>
                  <th
                    class="text-center"
                    style="min-width: 100px"
                  >
                    {{ $t('ตั้งค่ารายเลข') }}
                  </th>
                  <th
                    v-for="bt in betTypeColumns"
                    :key="'th-' + group.key + '-' + bt.value"
                    class="text-center"
                    style="min-width: 110px"
                  >
                    {{ bt.shortLabel }}
                  </th>
                  <th
                    class="text-center"
                    style="min-width: 90px"
                  />
                </tr>
              </thead>
              <tbody>
                <!-- แถว กรอกทุกแถว -->
                <tr class="fill-all-row">
                  <td
                    colspan="2"
                    class="align-middle font-weight-bold text-center"
                  >
                    {{ $t('กรอกทุกแถว') }}
                  </td>
                  <td
                    v-for="bt in betTypeColumns"
                    :key="'fill-' + group.key + '-' + bt.value"
                    class="p-1"
                  >
                    <!-- 4/5 ตัว: ไม่มีหวยในกลุ่มที่ใช้ประเภทนี้ได้ = ไม่มีอะไรให้คัดลอก -->
                    <div
                      v-if="!isGroupCapAvailable(group, bt.value)"
                      class="text-center text-muted"
                    >
                      —
                    </div>
                    <div
                      v-else
                      class="d-flex flex-column"
                    >
                      <b-form-input
                        :value="group.fillValues[bt.value]"
                        :disabled="!isGroupCapEditable(group, bt.value)"
                        type="number"
                        size="sm"
                        class="text-center mb-1"
                        :placeholder="$t('ไม่จำกัด')"
                        @input="onFillValueInput(group, bt.value, $event)"
                      />
                      <b-button
                        variant="success"
                        size="sm"
                        block
                        :disabled="!isGroupCapEditable(group, bt.value)"
                        @click="copyToAllInGroup(group, bt.value)"
                      >
                        {{ $t('ตัดลอก') }}
                      </b-button>
                    </div>
                  </td>
                  <td
                    class="text-center align-middle"
                    style="white-space: nowrap"
                  >
                    <b-button
                      variant="outline-secondary"
                      size="sm"
                      @click="resetGroupToOriginal(group)"
                    >
                      {{ $t('กลับค่าเดิม') }}
                    </b-button>
                  </td>
                </tr>

                <!-- แถวหวยแต่ละตัว -->
                <tr
                  v-for="huay in group.huays"
                  :key="huay.huayId"
                  :class="{ 'row-saving': huay.saving }"
                >
                  <td class="align-middle huay-name-cell">
                    <div
                      class="d-flex align-items-center"
                      style="white-space: nowrap"
                    >
                      <span
                        v-if="getCountryCode(huay.huayName)"
                        class="flag-icon flag-icon-rounded mr-1"
                        :class="`flag-icon-${getCountryCode(huay.huayName)}`"
                      />
                      <span>{{ huay.huayName }}</span>
                    </div>
                  </td>
                  <td
                    class="text-center align-middle"
                    style="white-space: nowrap"
                  >
                    <b-button
                      variant="primary"
                      size="sm"
                      @click="goToNumberCap(huay)"
                    >
                      {{ $t('ตั้งค่ารายเลข') }}
                    </b-button>
                  </td>
                  <td
                    v-for="bt in betTypeColumns"
                    :key="huay.huayId + '-' + bt.value"
                    class="p-1 cap-cell-td"
                  >
                    <!-- 4/5 ตัว: หวยที่ใช้ประเภทนี้ไม่ได้ไม่มีช่องให้ตั้ง -->
                    <div
                      v-if="!isCapAvailable(huay.huayId, bt.value)"
                      class="text-center text-muted"
                    >
                      —
                    </div>
                    <div
                      v-else
                      class="cap-cell"
                    >
                      <small class="original-value">
                        ({{ $t('ค่าเดิม') }}) {{ formatNumber(huay.originalCaps[bt.value]) }}
                      </small>
                      <b-form-input
                        :value="huay.caps[bt.value]"
                        :disabled="!isCapEditable(huay.huayId, bt.value)"
                        :title="isCapEditable(huay.huayId, bt.value) ? '' : $t('fourFiveDigit.locked')"
                        type="number"
                        size="sm"
                        class="text-center"
                        :placeholder="$t('ไม่จำกัด')"
                        :class="{ 'is-changed': huay.caps[bt.value] !== huay.originalCaps[bt.value] }"
                        @input="onCapInput(huay, bt.value, $event)"
                      />
                    </div>
                  </td>
                  <td class="text-center align-middle">
                    <b-button
                      v-if="huay.dirty"
                      variant="success"
                      size="sm"
                      :disabled="huay.saving"
                      @click="saveHuayCaps(huay)"
                    >
                      <b-spinner
                        v-if="huay.saving"
                        small
                      />
                      <span v-else>{{ $t('บันทึก') }}</span>
                    </b-button>
                    <feather-icon
                      v-else
                      icon="CheckIcon"
                      class="text-success"
                      size="18"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- ปุ่มบันทึกที่เลือกทั้งหมด -->
          <div class="text-center mb-3">
            <b-button
              variant="info"
              :disabled="!hasGroupDirty(group)"
              @click="saveAllInGroup(group)"
            >
              <feather-icon
                icon="SaveIcon"
                class="mr-50"
              />
              {{ $t('บันทึกที่เลือกทั้งหมด') }}
            </b-button>
          </div>
        </div>
      </b-tab>

      <!-- Tab 2: ตั้งค่าความเสี่ยงรายเลข -->
      <b-tab lazy>
        <template #title>
          <span class="d-none d-sm-inline">{{ $t('ตั้งค่าความเสี่ยงรายเลข') }}</span>
          <span class="d-sm-none">{{ $t('รายเลข') }}</span>
        </template>
        <div class="mb-2">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              lg="3"
              class="mb-1 mb-lg-0"
            >
              <v-select
                v-model="selectedHuay"
                :options="$tOptions(huayOptions)"
                :disabled="isLoading"
                :loading="isLoading"
                label="name"
                :placeholder="isLoading ? $t('กำลังโหลด...') : $t('เลือกหวย')"
              >
                <template #option="option">
                  <div class="d-flex align-items-center">
                    <span
                      v-if="getCountryCode(option.name)"
                      class="flag-icon flag-icon-rounded mr-1"
                      :class="`flag-icon-${getCountryCode(option.name)}`"
                    />
                    <span>{{ option.name }}</span>
                  </div>
                </template>
                <template #selected-option="option">
                  <div class="d-flex align-items-center">
                    <span
                      v-if="getCountryCode(option.name)"
                      class="flag-icon flag-icon-rounded mr-1"
                      :class="`flag-icon-${getCountryCode(option.name)}`"
                    />
                    <span>{{ option.name }}</span>
                  </div>
                </template>
              </v-select>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              lg="3"
              class="mb-1 mb-lg-0"
            >
              <v-select
                v-model="newNumberCap.betType"
                :options="$tOptions(numberCapTypeOptions)"
                label="label"
                :reduce="opt => opt.value"
                :placeholder="$t('ประเภท')"
              />
            </b-col>
            <b-col
              cols="12"
              sm="6"
              lg="2"
              class="mb-1 mb-lg-0"
            >
              <b-form-input
                v-model="newNumberCap.number"
                :placeholder="$t('เลข (เช่น 999)')"
              />
            </b-col>
            <b-col
              cols="12"
              sm="6"
              lg="2"
              class="mb-1 mb-lg-0"
            >
              <b-form-input
                v-model.number="newNumberCap.capAmount"
                type="number"
                :placeholder="$t('Cap Amount')"
              />
            </b-col>
            <b-col
              cols="12"
              sm="6"
              lg="2"
            >
              <b-button
                variant="success"
                @click="addNumberCap"
              >
                <feather-icon
                  icon="PlusIcon"
                  class="mr-50"
                />
                {{ $t('เพิ่ม') }}
              </b-button>
            </b-col>
          </b-row>
        </div>

        <div class="table-responsive">
          <table class="table table-bordered table-sm table-hover risk-cap-table">
            <thead class="thead-light">
              <tr>
                <th>{{ $t('เลข') }}</th>
                <th style="min-width: 60px">
                  {{ $t('ประเภท') }}
                </th>
                <th style="min-width: 120px">
                  {{ $t('Cap Amount') }}
                </th>
                <th style="min-width: 60px" />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in numberCaps"
                :key="item.id"
              >
                <td class="align-middle">
                  {{ item.number }}
                </td>
                <td class="align-middle">
                  {{ getBetTypeName(item.betType) }}
                </td>
                <td class="align-middle">
                  <span :class="{ 'cap-unlimited': item.capAmount === -1 }">
                    {{ formatCapAmount(item.capAmount) }}
                  </span>
                </td>
                <td class="align-middle">
                  <b-button
                    variant="flat-danger"
                    size="sm"
                    class="btn-icon"
                    @click="deleteNumberCap(item)"
                  >
                    <feather-icon icon="Trash2Icon" />
                  </b-button>
                </td>
              </tr>
              <tr v-if="numberCaps.length === 0">
                <td
                  colspan="4"
                  class="text-center text-muted"
                >
                  {{ selectedHuay ? $t('ไม่มีข้อมูล') : $t('กรุณาเลือกหวย') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-tab>

      <!-- Tab 3: แสดงค่าตั้งสู้สายล่าง -->
      <b-tab lazy>
        <template #title>
          <span class="d-none d-sm-inline">{{ $t('แสดงค่าตั้งสู้สายล่าง') }}</span>
          <span class="d-sm-none">{{ $t('สายล่าง') }}</span>
        </template>
        <div class="mb-2">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              lg="3"
              class="mb-1 mb-lg-0"
            >
              <v-select
                v-model="selectedHuay"
                :options="$tOptions(huayOptions)"
                :disabled="isLoading"
                :loading="isLoading"
                label="name"
                :placeholder="$t('เลือกหวย')"
              >
                <template #option="option">
                  <div class="d-flex align-items-center">
                    <span
                      v-if="getCountryCode(option.name)"
                      class="flag-icon flag-icon-rounded mr-1"
                      :class="`flag-icon-${getCountryCode(option.name)}`"
                    />
                    <span>{{ option.name }}</span>
                  </div>
                </template>
                <template #selected-option="option">
                  <div class="d-flex align-items-center">
                    <span
                      v-if="getCountryCode(option.name)"
                      class="flag-icon flag-icon-rounded mr-1"
                      :class="`flag-icon-${getCountryCode(option.name)}`"
                    />
                    <span>{{ option.name }}</span>
                  </div>
                </template>
              </v-select>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              lg="3"
              class="mb-1 mb-lg-0"
            >
              <b-form-input
                v-model="downlineFilter"
                :placeholder="$t('ค้นหา Agent...')"
                debounce="300"
              />
            </b-col>
          </b-row>
        </div>

        <!-- ตารางแสดงค่าสายล่าง -->
        <div class="table-responsive">
          <table class="table table-bordered table-sm risk-cap-table">
            <thead class="thead-light">
              <tr>
                <th style="min-width: 40px">
                  #
                </th>
                <th style="min-width: 100px">
                  Agent
                </th>
                <th style="min-width: 100px">
                  {{ $t('ชื่อ') }}
                </th>
                <th
                  v-for="bt in downlineBetTypeColumns"
                  :key="'th-dl-' + bt.value"
                  class="text-center"
                  style="min-width: 80px"
                >
                  {{ bt.shortLabel }}
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-if="!isLoadingDownline">
                <tr
                  v-for="(downlineAgent, index) in filteredDownlineCaps"
                  :key="downlineAgent.username"
                >
                  <td class="align-middle">
                    {{ index + 1 }}
                  </td>
                  <td class="align-middle">
                    <router-link
                      :to="{ path: '/agents/edit', query: { id: downlineAgent.username } }"
                      class="agent-link text-primary"
                    >
                      {{ downlineAgent.username }}
                    </router-link>
                  </td>
                  <td class="align-middle">
                    {{ downlineAgent.nickName || '-' }}
                  </td>
                  <td
                    v-for="bt in downlineBetTypeColumns"
                    :key="downlineAgent.username + '-' + bt.value"
                    :class="['text-center', 'align-middle', isUnlimitedCap(downlineAgent, bt.value) ? 'cap-unlimited' : '']"
                  >
                    {{ getDownlineCapValue(downlineAgent, bt.value) }}
                  </td>
                </tr>
              </template>
              <!-- Loading state -->
              <tr v-if="isLoadingDownline">
                <td
                  :colspan="3 + betTypeColumns.length"
                  class="text-center text-muted"
                >
                  <b-spinner
                    small
                    variant="primary"
                    class="mr-1"
                  />
                  {{ $t('กำลังโหลด...') }}
                </td>
              </tr>
              <!-- Empty state -->
              <tr v-else-if="filteredDownlineCaps.length === 0">
                <td
                  :colspan="3 + betTypeColumns.length"
                  class="text-center text-muted"
                >
                  {{ selectedHuay ? $t('ไม่พบข้อมูลสายล่าง') : $t('กรุณาเลือกหวย') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-tab>

      <!-- Tab 4: เลขอั้น (TransferWallet เท่านั้น) -->
      <b-tab
        v-if="isTransferWallet"
        lazy
      >
        <template #title>
          <span class="d-none d-sm-inline">{{ $t('เลขอั้น') }}</span>
          <span class="d-sm-none">{{ $t('เลขอั้น') }}</span>
        </template>

        <div class="d-flex align-items-center mb-2">
          <v-select
            v-model="selectedHuay"
            :options="$tOptions(huayOptions)"
            :disabled="isLoading"
            :loading="isLoading"
            label="name"
            :placeholder="isLoading ? $t('กำลังโหลด...') : $t('เลือกหวย')"
            style="min-width: 250px"
          >
            <template #option="option">
              <div class="d-flex align-items-center">
                <span
                  v-if="getCountryCode(option.name)"
                  class="flag-icon flag-icon-rounded mr-1"
                  :class="`flag-icon-${getCountryCode(option.name)}`"
                />
                <span>{{ option.name }}</span>
              </div>
            </template>
            <template #selected-option="option">
              <div class="d-flex align-items-center">
                <span
                  v-if="getCountryCode(option.name)"
                  class="flag-icon flag-icon-rounded mr-1"
                  :class="`flag-icon-${getCountryCode(option.name)}`"
                />
                <span>{{ option.name }}</span>
              </div>
            </template>
          </v-select>
          <b-form-checkbox
            v-if="!$store.state.app.isMaster($store) && !fixedPayLocked"
            v-model="isAutoUpdateFixedPay"
            name="check-button"
            class="ml-2 mb-0"
            switch
            inline
            @change="changeAutoUpdateFixedPay"
          >
            Auto Update Limit
          </b-form-checkbox>
        </div>

        <!-- BetType tabs -->
        <div class="mb-1">
          <b-button
            v-for="bt in fixedPayTypeTabs"
            :key="'fp-tab-' + bt.value"
            :variant="fixedPayBetTypeFilter === bt.value ? 'primary' : 'outline-primary'"
            size="sm"
            class="mr-50 mb-50"
            @click="changeFixedPayBetType(bt.value)"
          >
            {{ bt.label }}
          </b-button>
        </div>

        <div
          class="position-relative fixed-pay-table-shell"
          :class="{ 'fixed-pay-table-shell--loading': fixedPayLoading }"
        >
          <div
            v-if="fixedPayLoading"
            class="fixed-pay-overlay"
            :class="{ 'fixed-pay-overlay--opaque': fixedPayInitialLoad }"
          >
            <b-spinner
              small
              variant="secondary"
            />
          </div>
          <div class="table-responsive">
            <table class="table table-bordered table-sm table-hover risk-cap-table">
              <thead class="thead-light">
                <tr>
                  <th class="text-center">
                    {{ $t('เลข') }}
                  </th>
                  <th
                    v-for="pkg in ratePackages"
                    :key="'th-pkg-' + pkg.id"
                    class="text-center"
                    style="min-width: 120px"
                  >
                    {{ getPackageBaseRate(pkg) ? `${pkg.name} (${getPackageBaseRate(pkg)})` : pkg.name }}
                  </th>
                  <!-- อัตราจ่ายระดับเลข: ใช้กับโพยที่ไม่ได้เลือกแพ็กเกจ (Normal)
                       และเป็นค่าที่แพ็กเกจซึ่งเว้นว่างไว้จะสืบทอดไปใช้ -->
                  <th
                    class="text-center"
                    style="min-width: 120px"
                  >
                    {{ ratePackages.length > 0 ? $t('อัตราจ่าย (ไม่ใช้แพ็กเกจ)') : $t('อัตราจ่าย') }}
                  </th>
                  <th
                    v-if="!fixedPayReadOnly"
                    style="width: 50px"
                  />
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, index) in fixedPays"
                  :key="item.id || item._tempId"
                >
                  <td class="p-1">
                    <b-form-input
                      v-model="item.number"
                      size="sm"
                      :disabled="fixedPayReadOnly"
                      :maxlength="fixedPayNumberMaxLength"
                      class="text-center"
                      :placeholder="!item.id ? fixedPayNumberPlaceholder : ''"
                      :class="{
                        'is-changed': fixedPayOriginals[item.id] && item.number !== fixedPayOriginals[item.id].number,
                        'is-new': !item.id && item.number,
                      }"
                      @keypress="!/\d/.test($event.key) && $event.preventDefault()"
                    />
                  </td>
                  <td
                    v-for="pkg in ratePackages"
                    :key="(item.id || item._tempId) + '-pkg-' + pkg.id"
                    class="p-1"
                  >
                    <b-form-input
                      v-model.number="item.pkgRates[pkg.id]"
                      type="number"
                      size="sm"
                      :disabled="fixedPayReadOnly"
                      :class="{
                        'is-changed': isFixedPayFieldDirty(item, pkg.id),
                        'is-new': !item.id && item.pkgRates[pkg.id] != null,
                      }"
                    />
                  </td>
                  <td class="p-1">
                    <b-form-input
                      v-model.number="item.payRate"
                      type="number"
                      size="sm"
                      :disabled="fixedPayReadOnly"
                      :class="{
                        'is-changed': isFixedPayFieldDirty(item, null),
                        'is-new': !item.id && item.payRate != null,
                      }"
                    />
                  </td>
                  <td
                    v-if="!fixedPayReadOnly"
                    class="text-center align-middle"
                  >
                    <b-button
                      variant="flat-danger"
                      size="sm"
                      class="btn-icon"
                      @click="fixedPays.splice(index, 1)"
                    >
                      <feather-icon icon="Trash2Icon" />
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <!-- ตัวช่วยเพิ่มเลข -->
          <div
            v-if="genHelperOptions.length > 0 && !fixedPayReadOnly"
            class="gen-helper-panel mb-1"
          >
            <div
              class="gen-helper-toggle"
              @click="showGenHelper = !showGenHelper"
            >
              <feather-icon
                :icon="showGenHelper ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                size="16"
                class="mr-50"
              />
              <span>{{ $t('ตัวช่วยเพิ่มเลข') }}</span>
            </div>

            <div
              v-if="showGenHelper"
              class="gen-helper-body"
            >
              <!-- inputs + ปุ่ม gen -->
              <div class="d-flex flex-wrap align-items-end mb-75">
                <div
                  v-if="genNeedsInput"
                  class="gen-field mr-1 mb-50"
                >
                  <label class="gen-field-label">{{ $t('เลขต้นทาง') }}</label>
                  <b-form-input
                    v-model="genInput"
                    size="sm"
                    :maxlength="genInputMaxLength"
                    :placeholder="genInputPlaceholder"
                    class="text-center"
                    style="width: 120px"
                    @keypress="!/\d/.test($event.key) && $event.preventDefault()"
                  />
                </div>
                <div
                  v-for="pkg in ratePackages"
                  :key="'gen-pkg-' + pkg.id"
                  class="gen-field mr-1 mb-50"
                >
                  <label class="gen-field-label">{{ pkg.name }}</label>
                  <b-form-input
                    v-model.number="genPkgRates[pkg.id]"
                    type="number"
                    size="sm"
                    placeholder="0"
                    style="width: 120px"
                  />
                </div>
                <div class="gen-field mr-1 mb-50">
                  <label class="gen-field-label">
                    {{ ratePackages.length > 0 ? $t('อัตราจ่าย (ไม่ใช้แพ็กเกจ)') : $t('อัตราจ่าย') }}
                  </label>
                  <b-form-input
                    v-model.number="genPayRate"
                    type="number"
                    size="sm"
                    placeholder="0"
                    style="width: 120px"
                  />
                </div>
              </div>

              <div class="d-flex flex-wrap mb-50">
                <b-button
                  v-for="opt in genHelperOptions"
                  :key="'gen-' + opt.method"
                  :variant="opt.needsInput ? 'outline-info' : 'outline-success'"
                  size="sm"
                  class="mr-50 mb-50"
                  @click="generateNumbers(opt.method)"
                >
                  {{ $t(opt.label) }}
                </b-button>
              </div>

              <!-- Preview (โผล่เฉพาะตอนมีเลข) -->
              <div
                v-if="genPreview.length > 0"
                class="gen-preview-area"
              >
                <div class="gen-preview-numbers">
                  <span
                    v-for="num in genPreview"
                    :key="'preview-' + num"
                    class="badge mr-25 mb-25"
                    :class="isGenPreviewDuplicate(num) ? 'badge-light-warning' : 'badge-light-info'"
                  >
                    {{ num }}
                  </span>
                  <span class="small text-muted ml-25">
                    {{ genPreview.length }} {{ $t('เลข') }}
                    <span
                      v-if="genPreviewDuplicateCount > 0"
                      class="text-warning"
                    >({{ $t('ซ้ำ') }} {{ genPreviewDuplicateCount }})</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- ปุ่มด้านล่าง -->
          <div
            v-if="!fixedPayReadOnly"
            class="d-flex justify-content-center align-items-center mb-2"
          >
            <b-button
              v-if="genUndoStack.length > 0"
              variant="outline-warning"
              size="sm"
              class="mr-1"
              @click="undoLastGenAdd"
            >
              <feather-icon
                icon="CornerUpLeftIcon"
                class="mr-50"
              />
              {{ $t('ย้อนกลับ') }}
            </b-button>
            <b-button
              v-if="genPreview.length > 0"
              variant="primary"
              size="sm"
              class="mr-1"
              :disabled="genPreviewNewCount === 0"
              @click="addGenPreviewToTable"
            >
              <feather-icon
                icon="PlusIcon"
                class="mr-50"
              />
              {{ $t('เพิ่มลงตาราง') }} ({{ genPreviewNewCount }})
            </b-button>
            <b-button
              v-else
              variant="outline-primary"
              size="sm"
              class="mr-1"
              @click="addNewFixedPayRowWithUndo"
            >
              <feather-icon
                icon="PlusIcon"
                class="mr-50"
              />
              {{ $t('เพิ่มเลข') }}
            </b-button>
            <b-button
              variant="success"
              size="sm"
              class="mr-1"
              :disabled="!hasFixedPayChanges"
              @click="bulkSaveFixedPay"
            >
              <feather-icon
                icon="SaveIcon"
                class="mr-50"
              />
              {{ $t('บันทึก') }}
            </b-button>
            <b-button
              v-if="fixedPays.length < Object.keys(fixedPayOriginals).length"
              variant="outline-secondary"
              size="sm"
              @click="loadFixedPays"
            >
              <feather-icon
                icon="RotateCcwIcon"
                class="mr-50"
              />
              {{ $t('กลับค่าเดิม') }}
            </b-button>
          </div>
        </div>
      </b-tab>
    </b-tabs>
  </b-card>
</template>

<script>
import {
  BCard, BTabs, BTab, BRow, BCol, BFormInput,
  BButton, BAlert, BSpinner, BFormCheckbox,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import lottoService from '@/service/lottoService'
import agentService from '@/service/agentService'
import fourFiveDigitService from '@/service/fourFiveDigitService'
import {
  FOUR_FIVE_DIGIT_LABELS, FOUR_FIVE_DIGIT_SHORT_LABELS, FOUR_FIVE_DIGIT_TYPES,
  digitPermutations, fourFiveDigitLength, fourFiveDigitNumberError, huayAccessLists, isFourFiveDigit, isLockedFourFiveDigit,
} from '@/utils/betTypes'
import { toastMixin } from '@core/mixins/ui/toast'
import { countryFlagMixin } from '@core/mixins/ui/countryFlag'

export default {
  components: {
    BCard,
    BTabs,
    BTab,
    BRow,
    BCol,

    BFormInput,
    BButton,
    BAlert,
    BSpinner,
    BFormCheckbox,

    vSelect,
  },
  mixins: [toastMixin, countryFlagMixin],
  data() {
    const savedTab = localStorage.getItem('risk-cap-tab')
    return {
      isLoading: true,
      activeTab: savedTab ? parseInt(savedTab, 10) : 0,
      selectedHuay: null,
      huayOptions: [],
      walletType: null,
      isTransferWallet: localStorage.getItem('risk-cap-tw') === '1',

      // Tab 1: Huay caps (grouped by type)
      huayGroups: [],
      allCapsFromApi: [], // เก็บ caps ทั้งหมดจาก API

      // กลุ่มหวย mapping (ตาม API huay/label)
      huayGroupConfig: [
        { key: 'thai', name: 'หวยไทย', sources: ['goverment', 'bankStock'] },
        { key: 'thaiStock', name: 'หวยหุ้นไทย', sources: ['thaiStock'] },
        { key: 'stock', name: 'หวยหุ้นต่างประเทศ', sources: ['stock'] },
      ],
      huayLabelData: null, // เก็บข้อมูลจาก API huay/label

      // Tab 2: Number caps
      numberCaps: [],
      numberCapFields: [
        { key: 'number', label: 'เลข' },
        { key: 'betType', label: 'ประเภท', thStyle: { minWidth: '60px' } },
        { key: 'capAmount', label: 'Cap Amount', thStyle: { minWidth: '120px' } },
        { key: 'actions', label: '', thStyle: { minWidth: '60px' } },
      ],
      newNumberCap: {
        betType: null,
        number: '',
        capAmount: 0,
      },

      // Tab 3: Fixed pay (TransferWallet)
      fixedPays: [],
      fixedPayOriginals: {},
      fixedPaysCache: {},
      ratePackages: [],
      fixedPayBetTypeFilter: 1,
      fixedPayLoading: true,
      fixedPayInitialLoad: true,
      isAutoUpdateFixedPay: false,
      // สมาชิกของสายที่ Master ล็อกไว้ · แท็บเลขอั้นเป็นแบบอ่านอย่างเดียว
      fixedPayLocked: false,

      // ตัวช่วยเพิ่มเลข
      showGenHelper: false,
      genInput: '',
      genPayRate: null,
      genPkgRates: {},
      genPreview: [],
      genUndoStack: [], // stack ของ action ที่เพิ่ม [{tempIds: [...]}]

      // Tab 4: Downline caps
      downlineCaps: [],
      downlineFilter: '',
      isLoadingDownline: false,

      // 4/5 ตัว: { [huayId]: ประเภทที่ใช้ได้ } — ประเภทใหม่ต่อท้ายคอลัมน์ / ตัวเลือกด้านล่างเมื่อมีสิทธิ์
      fourFiveDigitAccess: {},
      // { [huayId]: ประเภทที่ติ๊กเปิดในหวยนั้นแล้ว } — เห็นแต่ยังไม่ติ๊ก = แก้ไม่ได้
      fourFiveDigitEditable: {},

      // BetType columns สำหรับ Tab 1 และ Tab 3
      betTypeColumns: [
        { value: 1, label: 'สามตัวบน', shortLabel: '3 ตัวบน' },
        { value: 2, label: 'สามตัวโต๊ด', shortLabel: '3 ตัวโต๊ด' },
        { value: 5, label: 'สองตัวบน', shortLabel: '2 ตัวบน' },
        { value: 6, label: 'สองตัวล่าง', shortLabel: '2 ตัวล่าง' },
        { value: 7, label: 'วิ่งบน', shortLabel: 'วิ่งบน' },
        { value: 8, label: 'วิ่งล่าง', shortLabel: 'วิ่งล่าง' },
      ],

      // BetType options สำหรับ Tab 2 (รวม "ทุกประเภท")
      betTypeOptions: [
        { value: -1, label: 'ทุกประเภท' },
        { value: 1, label: 'สามตัวบน' },
        { value: 2, label: 'สามตัวโต๊ด' },
        { value: 3, label: 'สามตัวหน้า' },
        { value: 4, label: 'สามตัวท้าย' },
        { value: 5, label: 'สองตัวบน' },
        { value: 6, label: 'สองตัวล่าง' },
        { value: 7, label: 'วิ่งบน' },
        { value: 8, label: 'วิ่งล่าง' },
      ],
      fixedPayBetTypeOptions: [
        { value: 1, label: 'สามตัวบน' },
        { value: 2, label: 'สามตัวโต๊ด' },
        { value: 3, label: 'สามตัวหน้า' },
        { value: 4, label: 'สามตัวท้าย' },
        { value: 5, label: 'สองตัวบน' },
        { value: 6, label: 'สองตัวล่าง' },
        { value: 7, label: 'วิ่งบน' },
        { value: 8, label: 'วิ่งล่าง' },
      ],
    }
  },
  computed: {
    agent() {
      return this.$store.state.app.userData?.username || ''
    },
    tabIndexNumberCaps() {
      return 1
    },
    tabIndexFixedPay() {
      return this.isTransferWallet ? 3 : -1
    },
    tabIndexDownline() {
      return 2
    },
    // 4/5 ตัว: ประเภทใหม่ที่หวยที่เลือกใช้ได้
    selectedHuayFourFiveDigitTypes() {
      return (this.selectedHuay && this.fourFiveDigitAccess[this.selectedHuay.id]) || []
    },
    // เพิ่มเลขอั้นได้เฉพาะประเภทที่ติ๊กเปิดในหวยที่เลือกแล้ว
    numberCapTypeOptions() {
      const editable = (this.selectedHuay && this.fourFiveDigitEditable[this.selectedHuay.id]) || []
      return this.betTypeOptions
        .filter(o => !isFourFiveDigit(o.value) || editable.includes(o.value))
    },
    // จ่ายตายตัวของประเภทที่เห็นแต่ยังไม่ติ๊กเปิดในหวยที่เลือก แสดงอย่างเดียว
    fixedPayReadOnly() {
      return this.fixedPayLocked || (isFourFiveDigit(this.fixedPayBetTypeFilter)
        && !(this.selectedHuay && this.isCapEditable(this.selectedHuay.id, this.fixedPayBetTypeFilter)))
    },
    fixedPayTypeTabs() {
      return this.fixedPayBetTypeOptions
        .filter(o => !isFourFiveDigit(o.value) || this.selectedHuayFourFiveDigitTypes.includes(o.value))
    },
    downlineBetTypeColumns() {
      return this.betTypeColumns
        .filter(o => !isFourFiveDigit(o.value) || this.selectedHuayFourFiveDigitTypes.includes(o.value))
    },
    fixedPayNumberPlaceholder() {
      const bt = this.fixedPayBetTypeOptions.find(o => o.value === this.fixedPayBetTypeFilter)
      if (!bt) return this.$t('เลข')
      if (isFourFiveDigit(bt.value)) return this.$t(`เลข ${fourFiveDigitLength(bt.value)} หลัก`)
      if ([1, 2, 3, 4].includes(bt.value)) return this.$t('เลข 3 หลัก')
      if ([5, 6].includes(bt.value)) return this.$t('เลข 2 หลัก')
      return this.$t('เลข 1 หลัก')
    },
    fixedPayNumberMaxLength() {
      const bt = this.fixedPayBetTypeFilter
      if (isFourFiveDigit(bt)) return fourFiveDigitLength(bt)
      if ([1, 2, 3, 4].includes(bt)) return 3
      if ([5, 6].includes(bt)) return 2
      return 1
    },
    hasFixedPayChanges() {
      const origCount = Object.keys(this.fixedPayOriginals).length
      const existingCount = this.fixedPays.filter(item => item.id).length
      const hasDeleted = existingCount !== origCount
      const hasDirty = this.fixedPays.some(item => item.id && this.isFixedPayDirty(item))
      const hasNew = this.fixedPays.some(item => !item.id && item.number && this.isValidFixedPayNumber(this.fixedPayBetTypeFilter, item.number))
      return hasDeleted || hasDirty || hasNew
    },

    // ตัวช่วยเพิ่มเลข: ตัวเลือกตาม betType
    genHelperOptions() {
      const bt = this.fixedPayBetTypeFilter
      if ([1, 2, 3, 4].includes(bt) || isFourFiveDigit(bt)) {
        return [
          { method: 'reverse', label: 'กลับเลข', needsInput: true },
        ]
      }
      if ([5, 6].includes(bt)) {
        return [
          { method: 'reverse', label: 'กลับเลข', needsInput: true },
          { method: 'frontSweep', label: 'รูดหน้า', needsInput: true },
          { method: 'backSweep', label: 'รูดหลัง', needsInput: true },
          { method: '19doors', label: '19 ประตู', needsInput: true },
          { method: 'double', label: 'เลขเบิ้ล', needsInput: false },
          { method: 'low', label: 'สองตัวต่ำ', needsInput: false },
          { method: 'high', label: 'สองตัวสูง', needsInput: false },
          { method: 'odd', label: 'สองตัวคี่', needsInput: false },
          { method: 'even', label: 'สองตัวคู่', needsInput: false },
        ]
      }
      return []
    },
    genNeedsInput() {
      return this.genHelperOptions.some(o => o.needsInput)
    },
    genInputMaxLength() {
      const bt = this.fixedPayBetTypeFilter
      if (isFourFiveDigit(bt)) return fourFiveDigitLength(bt)
      if ([1, 2, 3, 4].includes(bt)) return 3
      return 2
    },
    genInputPlaceholder() {
      const bt = this.fixedPayBetTypeFilter
      if (isFourFiveDigit(bt)) return this.$t(`เลข ${fourFiveDigitLength(bt)} หลัก`)
      if ([1, 2, 3, 4].includes(bt)) return this.$t('เลข 3 หลัก')
      return this.$t('เลข 1-2 หลัก')
    },
    genExistingNumbers() {
      return new Set(this.fixedPays.filter(r => r.number).map(r => r.number))
    },
    genPreviewDuplicateCount() {
      return this.genPreview.filter(n => this.genExistingNumbers.has(n)).length
    },
    genPreviewNewCount() {
      return this.genPreview.filter(n => !this.genExistingNumbers.has(n)).length
    },

    filteredDownlineCaps() {
      let list = this.downlineCaps
      if (this.downlineFilter) {
        const filter = this.downlineFilter.toLowerCase()
        list = list.filter(item => item.username?.toLowerCase().includes(filter) || item.nickName?.toLowerCase().includes(filter))
      }
      // Sort: มี caps ขึ้นก่อน
      return [...list].sort((a, b) => (b.caps?.length || 0) - (a.caps?.length || 0))
    },
  },
  watch: {
    async selectedHuay(val) {
      localStorage.setItem('risk-cap-huay', val?.id || '')
      if (isFourFiveDigit(this.fixedPayBetTypeFilter)
        && !this.selectedHuayFourFiveDigitTypes.includes(this.fixedPayBetTypeFilter)) {
        this.fixedPayBetTypeFilter = 1
      }
      this.fixedPaysCache = {}
      this.genPreview = []
      this.genInput = ''
      this.genPayRate = null
      this.genPkgRates = {}
      this.genUndoStack = []
      await this.loadRatePackages()
      // Tab 2 และ 3 ต้อง reload เมื่อเปลี่ยนหวย
      if (this.activeTab === this.tabIndexNumberCaps) this.loadNumberCaps()
      else if (this.activeTab === this.tabIndexFixedPay) this.loadFixedPays()
      else if (this.activeTab === this.tabIndexDownline) this.loadDownlineCaps()
      // Auto update status เป็น per-huay โหลดตอนเปลี่ยนหวยเท่านั้น
      // สายที่ล็อกไม่มีสวิตช์นี้ในหน้าจอ จึงไม่ต้องอ่านสถานะ
      if (val && !this.$store.state.app.isMaster(this.$store) && !this.fixedPayLocked) {
        this.loadFixedPayAutoUpdate()
      }
    },
  },
  async mounted() {
    // สถานะล็อกต้องพร้อมก่อนเฟรมแรกของแท็บเลขอั้น มิฉะนั้นปุ่มแก้ไขจะถูกวาดให้ผู้ที่ไม่มีสิทธิ์
    // แล้วหายไปหลังคำตอบมาถึง · อ่านคู่กับสถานะกระเป๋าเงินเพื่อไม่ให้เพิ่มรอบรอ
    await Promise.all([this.loadWalletStatus(), this.loadFixedPayLockState(), this.loadFourFiveDigitAccess()])
    this.loadHuayOptions()
  },
  methods: {
    // 4/5 ตัว: ต่อท้ายคอลัมน์ / ตัวเลือกด้วยประเภทใหม่ที่ใช้ได้ในหวยใดหวยหนึ่ง (หน้านี้มีแต่หวยปกติ ไม่รวมยี่กี)
    async loadFourFiveDigitAccess() {
      const { visible, editable } = await fourFiveDigitService.settingAccess()
      this.fourFiveDigitAccess = visible
      this.fourFiveDigitEditable = editable
      const lists = huayAccessLists(this.fourFiveDigitAccess)
      FOUR_FIVE_DIGIT_TYPES.filter(type => lists.some(list => list.includes(type))).forEach(type => {
        this.betTypeColumns.push({ value: type, label: FOUR_FIVE_DIGIT_LABELS[type], shortLabel: FOUR_FIVE_DIGIT_SHORT_LABELS[type] })
        this.betTypeOptions.push({ value: type, label: FOUR_FIVE_DIGIT_LABELS[type] })
        this.fixedPayBetTypeOptions.push({ value: type, label: FOUR_FIVE_DIGIT_LABELS[type] })
      })
    },
    isCapAvailable(huayId, betType) {
      return !isFourFiveDigit(betType) || (this.fourFiveDigitAccess[huayId] || []).includes(betType)
    },
    isCapEditable(huayId, betType) {
      return !isLockedFourFiveDigit(betType, this.fourFiveDigitEditable[huayId])
    },
    isGroupCapEditable(group, betType) {
      return group.huays.some(huay => this.isCapEditable(huay.huayId, betType))
    },
    isGroupCapAvailable(group, betType) {
      return !isFourFiveDigit(betType) || group.huays.some(huay => this.isCapAvailable(huay.huayId, betType))
    },
    async loadWalletStatus() {
      const isMaster = this.$store.state.app.isMaster(this.$store)
      try {
        const res = await agentService.getAuthen('wallet-status')
        if (res.status === 200) {
          this.walletType = res.data.walletType
        }
        this.isTransferWallet = isMaster || this.walletType === 1
      } catch {
        this.isTransferWallet = isMaster
      } finally {
        localStorage.setItem('risk-cap-tw', this.isTransferWallet ? '1' : '0')
        this.normalizeActiveTab()
        if (!this.isLoading) {
          this.loadDataForCurrentTab()
        }
      }
    },

    normalizeActiveTab() {
      const maxTab = this.isTransferWallet ? 3 : 2
      if (this.activeTab > maxTab || this.activeTab < 0) {
        this.activeTab = 0
        localStorage.setItem('risk-cap-tab', this.activeTab)
      }
    },

    changeTab(e) {
      this.activeTab = e
      localStorage.setItem('risk-cap-tab', this.activeTab)
      this.loadDataForCurrentTab()
    },

    async loadHuayOptions() {
      const res = await lottoService.getAuthen('huay/dict')
      if (res.status === 200) {
        this.huayOptions = res.data.filter(h => h.type !== 5)
        const huayId = localStorage.getItem('risk-cap-huay')
        if (huayId) {
          this.selectedHuay = this.huayOptions.find(h => h.id === huayId) || null
        }
      }
      this.isLoading = false
      await this.loadDataForCurrentTab()
    },

    loadDataForCurrentTab() {
      if (this.activeTab === 0) this.loadAllHuayCaps()
      else if (this.activeTab === this.tabIndexNumberCaps) this.loadNumberCaps()
      else if (this.activeTab === this.tabIndexFixedPay) this.loadFixedPays()
      else if (this.activeTab === this.tabIndexDownline) this.loadDownlineCaps()
    },

    // ==================== Tab 1: ตั้งค่าความเสี่ยง (Grouped by Type) ====================

    async loadAllHuayCaps() {
      // ดึง huay/label เพื่อได้กลุ่มหวยที่ถูกต้อง
      const labelRes = await lottoService.getAuthen('huay/label')
      if (labelRes.status === 200) {
        this.huayLabelData = labelRes.data
      }

      // ดึง caps ทั้งหมดของ agent
      const capsRes = await lottoService.getAuthen(`api/riskcap/${this.agent}`)
      if (capsRes.status === 200) {
        this.allCapsFromApi = capsRes.data.filter(c => c.number == null) // เฉพาะ default caps
      }

      if (!this.huayLabelData) return

      // สร้าง huayGroups จาก huayLabelData
      this.huayGroups = this.huayGroupConfig
        .map(groupConfig => {
          // หาหวยในกลุ่มนี้จาก API huay/label (รวมจาก sources ทั้งหมด)
          const huaysInGroup = []
          groupConfig.sources.forEach(sourceKey => {
            const sourceHuays = this.huayLabelData[sourceKey] || []
            huaysInGroup.push(...sourceHuays)
          })

          if (huaysInGroup.length === 0) return null

          // สร้าง huays data
          const huays = huaysInGroup.map(huay => {
            const huayCaps = this.allCapsFromApi.filter(c => c.huayId === huay.id)

            // สร้าง caps object
            // null = ไม่จำกัด (ไม่มี record), 0 = ไม่รับเลย, > 0 = รับตามที่กำหนด
            const caps = {}
            const originalCaps = {}
            this.betTypeColumns.forEach(bt => {
              const existingCap = huayCaps.find(c => c.betType === bt.value)
              // ถ้าไม่มี record หรือ capAmount = -1 → แสดงเป็น null (ไม่จำกัด)
              let val = null
              if (existingCap) {
                val = existingCap.capAmount === -1 ? null : existingCap.capAmount
              }
              caps[bt.value] = val
              originalCaps[bt.value] = val
            })

            return {
              huayId: huay.id,
              huayName: huay.name,
              caps,
              originalCaps,
              dirty: false,
              saving: false,
            }
          })

          // Init fillValues สำหรับกลุ่มนี้
          const fillValues = {}
          this.betTypeColumns.forEach(bt => {
            fillValues[bt.value] = null
          })

          return {
            key: groupConfig.key,
            name: groupConfig.name,
            huays,
            fillValues,
          }
        })
        .filter(g => g !== null)
    },

    // Handle cap input (รองรับค่าว่าง = ไม่จำกัด)
    onCapInput(huay, betType, value) {
      // ถ้าว่างหรือไม่ใช่ตัวเลข → null (ไม่จำกัด)
      // ถ้าเป็นตัวเลข → แปลงเป็น number
      let newValue = null
      if (value !== '' && value !== null && value !== undefined) {
        const num = parseFloat(value)
        if (!Number.isNaN(num) && num >= 0) {
          newValue = num
        }
      }
      this.$set(huay.caps, betType, newValue)
      this.$set(huay, 'dirty', true)
    },

    // Handle fill value input (รองรับค่าว่าง = ไม่จำกัด)
    onFillValueInput(group, betType, value) {
      let newValue = null
      if (value !== '' && value !== null && value !== undefined) {
        const num = parseFloat(value)
        if (!Number.isNaN(num) && num >= 0) {
          newValue = num
        }
      }
      this.$set(group.fillValues, betType, newValue)
    },
    // Check if group has any dirty huay
    hasGroupDirty(group) {
      return group.huays.some(h => h.dirty)
    },

    // Copy value to all huays in group (for specific betType)
    // รองรับค่าว่าง (null = ไม่จำกัด)
    copyToAllInGroup(group, betType) {
      const val = group.fillValues[betType]
      // ถ้ายังไม่ได้กรอกอะไรเลย (undefined) → ไม่ทำอะไร
      if (val === undefined) return

      // val อาจเป็น null (ไม่จำกัด), 0 (ไม่รับ), หรือ > 0 (รับตามที่กำหนด)
      group.huays.filter(huay => this.isCapEditable(huay.huayId, betType)).forEach(huay => {
        this.$set(huay.caps, betType, val)
        this.$set(huay, 'dirty', true)
      })
    },

    // Reset all huays in group to original values
    resetGroupToOriginal(group) {
      group.huays.forEach(huay => {
        this.betTypeColumns.forEach(bt => {
          this.$set(huay.caps, bt.value, huay.originalCaps[bt.value])
        })
        this.$set(huay, 'dirty', false)
      })
      // Clear fill values
      this.betTypeColumns.forEach(bt => {
        this.$set(group.fillValues, bt.value, null)
      })
      this.toastSuccess(this.$t('กลับค่าเดิมเรียบร้อย'))
    },

    // Go to Tab 2 with selected huay
    goToNumberCap(huay) {
      this.selectedHuay = this.huayOptions.find(h => h.id === huay.huayId)
      this.activeTab = 1 // Switch to Tab 2
      localStorage.setItem('risk-cap-tab', '1')
    },

    // Save single huay caps (ใช้ Bulk API)
    async saveHuayCaps(huay) {
      this.$set(huay, 'saving', true)

      try {
        // Build caps array สำหรับ 1 huay (ทุก betType)
        // null/empty = ไม่จำกัด (ลบ record), 0 = ไม่รับเลย, > 0 = รับตามที่กำหนด
        const caps = this.betTypeColumns.filter(bt => this.isCapAvailable(huay.huayId, bt.value)).map(bt => {
          const val = huay.caps[bt.value]
          return {
            agent: this.agent,
            huayId: huay.huayId,
            betType: bt.value,
            number: null,
            capAmount: (val === null || val === undefined || val === '') ? null : val,
            isEnabled: true,
          }
        })

        // เรียก Bulk API (1 request แทน 6 requests)
        const res = await lottoService.postAuthen('api/riskcap/bulk', {
          agent: this.agent,
          caps,
        })

        if (res.status === 200) {
          // Update original caps
          this.betTypeColumns.forEach(bt => {
            this.$set(huay.originalCaps, bt.value, huay.caps[bt.value])
          })
          this.$set(huay, 'dirty', false)
          this.toastSuccess(this.$t('บันทึก {name} สำเร็จ', { name: huay.huayName }))
        }
      } catch (err) {
        this.toastError(this.$t('เกิดข้อผิดพลาด'))
      } finally {
        this.$set(huay, 'saving', false)
      }
    },

    // Save all dirty huays in group (ใช้ Bulk API)
    async saveAllInGroup(group) {
      const dirtyHuays = group.huays.filter(h => h.dirty)
      if (dirtyHuays.length === 0) return

      // Mark all as saving
      dirtyHuays.forEach(huay => {
        this.$set(huay, 'saving', true)
      })

      try {
        // Build caps array สำหรับ bulk API
        // null/empty = ไม่จำกัด (ลบ record), 0 = ไม่รับเลย, > 0 = รับตามที่กำหนด
        const caps = []
        dirtyHuays.forEach(huay => {
          this.betTypeColumns.filter(bt => this.isCapAvailable(huay.huayId, bt.value)).forEach(bt => {
            const val = huay.caps[bt.value]
            caps.push({
              agent: this.agent,
              huayId: huay.huayId,
              betType: bt.value,
              number: null,
              capAmount: (val === null || val === undefined || val === '') ? null : val,
              isEnabled: true,
            })
          })
        })

        // เรียก Bulk API (1 request แทน N requests)
        const res = await lottoService.postAuthen('api/riskcap/bulk', {
          agent: this.agent,
          caps,
        })

        if (res.status === 200) {
          // Update original caps และ clear dirty
          dirtyHuays.forEach(huay => {
            this.betTypeColumns.forEach(bt => {
              this.$set(huay.originalCaps, bt.value, huay.caps[bt.value])
            })
            this.$set(huay, 'dirty', false)
          })
          this.toastSuccess(this.$t('บันทึกทั้งหมดใน {name} สำเร็จ ({count} รายการ)', {
            name: group.name,
            count: res.data.total,
          }))
        }
      } catch (err) {
        this.toastError(this.$t('เกิดข้อผิดพลาด'))
      } finally {
        dirtyHuays.forEach(huay => {
          this.$set(huay, 'saving', false)
        })
      }
    },

    // Format number with comma
    formatNumber(val) {
      if (val === null || val === undefined) return 'ไม่จำกัด'
      return val.toLocaleString()
    },

    // Tab 2: Load number caps
    async loadNumberCaps() {
      if (!this.selectedHuay) return
      const res = await lottoService.getAuthen(`api/riskcap/${this.agent}`)
      if (res.status === 200) {
        const huayId = this.selectedHuay.id
        // Filter caps where Number is NOT null
        this.numberCaps = res.data.filter(c => c.number != null && (c.huayId === huayId || c.huayId == null))
      }
    },

    async addNumberCap() {
      if (!this.newNumberCap.number || this.newNumberCap.betType == null) {
        this.toastWarning(this.$t('กรุณากรอกข้อมูลให้ครบ'))
        return
      }
      if (isFourFiveDigit(this.newNumberCap.betType)) {
        const error = fourFiveDigitNumberError(this.newNumberCap.betType, this.newNumberCap.number)
        if (error) {
          this.toastWarning(this.$t(error))
          return
        }
      }

      const payload = {
        agent: this.agent,
        huayId: this.selectedHuay?.id || null,
        betType: this.newNumberCap.betType,
        number: this.newNumberCap.number,
        capAmount: this.newNumberCap.capAmount,
        isEnabled: true,
      }

      const res = await lottoService.postAuthen('api/riskcap', payload)
      if (res.status === 200) {
        this.toastSuccess(this.$t('เพิ่มสำเร็จ'))
        this.newNumberCap = { betType: null, number: '', capAmount: 0 }
        await this.loadNumberCaps()
      }
    },

    async deleteNumberCap(item) {
      const res = await lottoService.deleteAuthen(`api/riskcap/${item.id}`)
      if (res.status === 200) {
        this.toastSuccess(this.$t('ลบสำเร็จ'))
        await this.loadNumberCaps()
      }
    },

    // Load rate packages for selected huay
    async loadRatePackages() {
      if (!this.selectedHuay) {
        this.ratePackages = []
        return
      }
      try {
        const res = await lottoService.getAuthen(`huay-rate-package?huayId=${this.selectedHuay.id}`)
        if (res.status === 200) {
          this.ratePackages = res.data?.list || []
        }
      } catch {
        this.ratePackages = []
      }
    },

    changeFixedPayBetType(value) {
      // Cache เฉพาะเมื่อมี unsaved changes
      if (this.hasFixedPayChanges) {
        this.fixedPaysCache[this.fixedPayBetTypeFilter] = {
          fixedPays: this.fixedPays,
          fixedPayOriginals: this.fixedPayOriginals,
        }
      } else {
        delete this.fixedPaysCache[this.fixedPayBetTypeFilter]
      }
      this.fixedPayBetTypeFilter = value
      this.genPreview = []
      this.genInput = ''
      this.genUndoStack = []
      this.loadFixedPays()
    },

    // Tab 3: Fixed pay (TransferWallet)
    async loadFixedPays() {
      if (!this.selectedHuay || !this.isTransferWallet) return
      this.fixedPayLoading = true
      const minDelay = new Promise(resolve => setTimeout(resolve, 100))
      try {
        const [res] = await Promise.all([
          lottoService.getAuthen(`api/limit-fixed-pay/${this.agent}?huayId=${this.selectedHuay.id}&type=${this.fixedPayBetTypeFilter}`),
          minDelay,
        ])
        if (res.status === 200) {
          const data = res.data || []
          // Transform: เพิ่ม pkgRates flat object สำหรับ v-model binding
          this.fixedPays = data.map(item => {
            const pkgRates = {}
            this.ratePackages.forEach(pkg => {
              const rate = item.packageRates?.find(r => r.packageId === pkg.id)
              // ไม่มี entry = แพ็กเกจนี้สืบทอดอัตราจ่ายระดับเลข → ปล่อยว่าง ไม่ใช่ 0
              // (0 แปลว่า "ปิดรับเลขนี้" คนละความหมายกัน)
              pkgRates[pkg.id] = rate ? rate.payRate : null
            })
            return { ...item, pkgRates }
          })
          // สร้าง originals จาก API response
          const freshOriginals = {}
          this.fixedPays.forEach(item => {
            freshOriginals[item.id] = {
              number: item.number,
              payRate: item.payRate,
              pkgRates: { ...item.pkgRates },
            }
          })

          // เช็ค cache: ถ้า API ยังตรงกับ originals ที่ cache ไว้ → restore user changes
          const cached = this.fixedPaysCache[this.fixedPayBetTypeFilter]
          if (cached && this.isOriginalsMatch(freshOriginals, cached.fixedPayOriginals)) {
            this.fixedPays = cached.fixedPays
            this.fixedPayOriginals = cached.fixedPayOriginals
          } else {
            this.fixedPayOriginals = freshOriginals
            if (!this.fixedPayReadOnly) this.addNewFixedPayRow()
          }
          delete this.fixedPaysCache[this.fixedPayBetTypeFilter]
        }
      } finally {
        this.fixedPayLoading = false
        this.fixedPayInitialLoad = false
      }
    },

    // สถานะนี้ผูกกับสิทธิ์ของผู้เรียก ไม่ใช่กับหวย จึงอ่านครั้งเดียวตอนเปิดหน้า
    async loadFixedPayLockState() {
      try {
        const res = await lottoService.getAuthen('api/limit-fixed-pay/lock-state')
        if (res.status === 200) {
          this.fixedPayLocked = res.data?.locked === true
        }
      } catch {
        this.fixedPayLocked = false
      }
    },

    async loadFixedPayAutoUpdate() {
      if (!this.selectedHuay) return
      const res = await lottoService.getAuthen(`api/limit-fixed-pay/auto/${this.selectedHuay.id}`)
      if (res.status === 200) {
        this.isAutoUpdateFixedPay = res.data
      }
    },

    async changeAutoUpdateFixedPay() {
      if (!this.selectedHuay) return
      const nextValue = this.isAutoUpdateFixedPay
      const res = await lottoService.putAuthen(`api/limit-fixed-pay/auto/${this.selectedHuay.id}`, {
        isAutoUpdate: nextValue,
      })
      if (res.status !== 200) {
        this.isAutoUpdateFixedPay = !nextValue
        this.toastError(this.$t('เกิดข้อผิดพลาด'))
      }
    },

    // ช่องแพ็กเกจมีค่าจริงไหม — ว่าง/null = สืบทอดค่าระดับเลข, 0 = ปิดรับ (คนละความหมาย)
    hasPkgRateValue(value) {
      return value !== null && value !== undefined && value !== ''
    },

    // คีย์เทียบค่าที่แยก "ว่าง" ออกจาก "0" ได้
    pkgRateKey(value) {
      return this.hasPkgRateValue(value) ? String(Number(value)) : ''
    },

    // เทียบ originals 2 ชุด ว่าเหมือนกันไหม (API ยังเหมือนเดิมหรือไม่)
    isOriginalsMatch(fresh, cached) {
      const freshKeys = Object.keys(fresh)
      const cachedKeys = Object.keys(cached)
      if (freshKeys.length !== cachedKeys.length) return false
      return freshKeys.every(id => {
        const f = fresh[id]
        const c = cached[id]
        if (!c) return false
        if (f.number !== c.number || f.payRate !== c.payRate) return false
        const fPkgKeys = Object.keys(f.pkgRates || {})
        return fPkgKeys.every(k => this.pkgRateKey(f.pkgRates[k]) === this.pkgRateKey(c.pkgRates[k]))
      })
    },

    addNewFixedPayRow() {
      const pkgRates = {}
      this.ratePackages.forEach(pkg => { pkgRates[pkg.id] = null })
      const tempId = `new-${Date.now()}-${this.fixedPays.length}`
      this.fixedPays.push({
        _tempId: tempId,
        number: '',
        payRate: null,
        pkgRates,
      })
      return tempId
    },

    addNewFixedPayRowWithUndo() {
      const tempId = this.addNewFixedPayRow()
      this.genUndoStack.push({ tempIds: [tempId] })
    },

    // Dirty check: เทียบค่าปัจจุบันกับ original
    isFixedPayDirty(item) {
      const orig = this.fixedPayOriginals[item.id]
      if (!orig) return false
      if (item.number !== orig.number) return true
      if (item.payRate !== orig.payRate) return true
      return this.ratePackages.some(
        pkg => this.pkgRateKey(item.pkgRates[pkg.id]) !== this.pkgRateKey(orig.pkgRates[pkg.id]),
      )
    },

    // Dirty check per field (สำหรับ highlight input)
    isFixedPayFieldDirty(item, packageId) {
      const orig = this.fixedPayOriginals[item.id]
      if (!orig) return false
      if (packageId != null) {
        return this.pkgRateKey(item.pkgRates[packageId]) !== this.pkgRateKey(orig.pkgRates[packageId])
      }
      return item.payRate !== orig.payRate
    },

    // Bulk save: Replace strategy — ส่ง ALL rows (existing + new) → backend ลบเดิม + insert ใหม่ทั้งหมด
    async bulkSaveFixedPay() {
      if (!this.selectedHuay) {
        this.toastWarning(this.$t('กรุณาเลือกหวย'))
        return
      }

      // การบันทึกเป็นแบบ replace ทั้งชุด ถ้ารายการแพ็กเกจโหลดไม่สำเร็จ payload จะไม่มี
      // packageRates เลย แล้วค่าอั้นรายแพ็กเกจเดิมจะถูกลบทิ้งทั้งหมด → เลขที่อั้นกลับมารับเรทเต็ม
      const hasExistingPackageRates = this.fixedPays.some(item => item.packageRates?.length > 0)
      if (this.ratePackages.length === 0 && hasExistingPackageRates) {
        this.toastError(this.$t('โหลดรายการแพ็กเกจไม่สำเร็จ กรุณารีเฟรชหน้าก่อนบันทึก'))
        return
      }

      // สร้าง items จาก fixedPays ทั้ง array (existing + new รวมกันแล้ว)
      const items = []
      const missingPayRate = []
      this.fixedPays.forEach(item => {
        if (!item.number || !this.isValidFixedPayNumber(this.fixedPayBetTypeFilter, item.number)) return
        // อัตราจ่ายระดับเลขต้องกรอกเสมอ — เป็นค่าที่โพย Normal ใช้ และเป็นค่าที่แพ็กเกจ
        // ซึ่งไม่ได้กรอกจะสืบทอดไป · เว้นว่างแล้วแปลงเป็น 0 = ปิดรับเลขนั้นเงียบ ๆ ทั้งโพย Normal
        // และทุกแพ็กเกจที่เว้นว่าง จึงต้องบังคับให้กรอก ไม่ใช่เดาแทนผู้ใช้
        if (!this.hasPkgRateValue(item.payRate)) {
          missingPayRate.push(item.number)
          return
        }
        // เว้นว่าง = ไม่สร้าง entry = สืบทอดค่าระดับเลข (กรอก 0 = ตั้งใจปิดรับเฉพาะแพ็กเกจนั้น)
        const packageRates = this.ratePackages
          .filter(pkg => this.hasPkgRateValue(item.pkgRates[pkg.id]))
          .map(pkg => ({ packageId: pkg.id, payRate: Number(item.pkgRates[pkg.id]) }))
        items.push({
          number: item.number,
          payRate: Number(item.payRate),
          packageRates: packageRates.length > 0 ? packageRates : null,
        })
      })

      if (missingPayRate.length > 0) {
        this.toastWarning(this.$t('กรุณากรอกอัตราจ่าย (ไม่ใช้แพ็กเกจ) ของเลข {numbers} — เว้นว่างจะกลายเป็นปิดรับเลขนั้น', {
          numbers: missingPayRate.join(', '),
        }))
        return
      }

      if (items.length === 0) {
        this.toastWarning(this.$t('ไม่มีข้อมูลที่ต้องบันทึก'))
        return
      }

      this.fixedPayLoading = true
      try {
        const res = await lottoService.postAuthen('api/limit-fixed-pay/bulk', {
          huayId: this.selectedHuay.id,
          type: this.fixedPayBetTypeFilter,
          items,
        })
        if (res.status === 200) {
          this.toastSuccess(this.$t('บันทึกสำเร็จ {count} รายการ', { count: res.data.total }))
          delete this.fixedPaysCache[this.fixedPayBetTypeFilter]
          await this.loadFixedPays()
        } else {
          this.toastError(res.data || this.$t('เกิดข้อผิดพลาด'))
        }
      } catch (err) {
        const msg = err?.response?.data || 'เกิดข้อผิดพลาด'
        this.toastError(msg)
      } finally {
        this.fixedPayLoading = false
      }
    },

    getPackageBaseRate(pkg) {
      const bt = this.fixedPayBetTypeFilter
      if (!pkg.betTypeRates) return null
      const btr = pkg.betTypeRates.find(r => r.type === bt)
      return btr ? btr.payRate : null
    },

    isValidFixedPayNumber(type, number) {
      if (!type || !number) return false
      const { length } = number
      if (isFourFiveDigit(type)) return fourFiveDigitNumberError(type, number) === ''
      if (type === 1 || type === 2 || type === 3 || type === 4) return length === 3
      if (type === 5 || type === 6) return length === 2
      if (type === 7 || type === 8) return length === 1
      return false
    },

    formatPayRate(value) {
      if (value === 0) return '0'
      if (value == null) return '-'
      return value.toLocaleString()
    },

    // ==================== ตัวช่วยเพิ่มเลข ====================

    generateNumbers(method) {
      const bt = this.fixedPayBetTypeFilter
      const input = this.genInput.trim()
      let numbers = []

      switch (method) {
        case 'reverse':
          if (isFourFiveDigit(bt)) {
            const error = fourFiveDigitNumberError(bt, input)
            if (error) {
              this.toastWarning(this.$t(error))
              return
            }
            numbers = digitPermutations(input)
          } else if ([1, 2, 3, 4].includes(bt)) {
            if (input.length !== 3) {
              this.toastWarning(this.$t('กรุณากรอกเลข 3 หลัก'))
              return
            }
            numbers = this.getThreeDigitPermutations(input)
          } else {
            if (input.length !== 2) {
              this.toastWarning(this.$t('กรุณากรอกเลข 2 หลัก'))
              return
            }
            numbers = this.getTwoDigitPermutations(input)
          }
          break
        case 'frontSweep':
          if (input.length !== 1) {
            this.toastWarning(this.$t('กรุณากรอกเลข 1 หลัก'))
            return
          }
          for (let i = 0; i <= 9; i += 1) {
            numbers.push(String(i) + input)
          }
          break
        case 'backSweep':
          if (input.length !== 1) {
            this.toastWarning(this.$t('กรุณากรอกเลข 1 หลัก'))
            return
          }
          for (let i = 0; i <= 9; i += 1) {
            numbers.push(input + String(i))
          }
          break
        case '19doors':
          if (input.length !== 1) {
            this.toastWarning(this.$t('กรุณากรอกเลข 1 หลัก'))
            return
          }
          {
            const set = new Set()
            for (let i = 0; i <= 9; i += 1) {
              set.add(String(i) + input)
            }
            for (let i = 0; i <= 9; i += 1) {
              set.add(input + String(i))
            }
            numbers = Array.from(set)
          }
          break
        case 'double':
          for (let i = 0; i <= 9; i += 1) {
            numbers.push(String(i) + String(i))
          }
          break
        case 'low':
          for (let i = 0; i <= 4; i += 1) {
            for (let j = 0; j <= 9; j += 1) {
              numbers.push(String(i) + String(j))
            }
          }
          break
        case 'high':
          for (let i = 5; i <= 9; i += 1) {
            for (let j = 0; j <= 9; j += 1) {
              numbers.push(String(i) + String(j))
            }
          }
          break
        case 'odd':
          for (let i = 0; i <= 9; i += 1) {
            for (let j = 0; j <= 9; j += 1) {
              if (j % 2 === 1) {
                numbers.push(String(i) + String(j))
              }
            }
          }
          break
        case 'even':
          for (let i = 0; i <= 9; i += 1) {
            for (let j = 0; j <= 9; j += 1) {
              if (j % 2 === 0) {
                numbers.push(String(i) + String(j))
              }
            }
          }
          break
        default:
          break
      }

      this.genPreview = numbers
    },

    getThreeDigitPermutations(num) {
      const t = String(num).padStart(3, '0')
      const perms = new Set([
        t[0] + t[1] + t[2],
        t[0] + t[2] + t[1],
        t[1] + t[0] + t[2],
        t[1] + t[2] + t[0],
        t[2] + t[0] + t[1],
        t[2] + t[1] + t[0],
      ])
      return Array.from(perms)
    },

    getTwoDigitPermutations(num) {
      const t = String(num).padStart(2, '0')
      if (t[0] === t[1]) return [t]
      return [t[0] + t[1], t[1] + t[0]]
    },

    isGenPreviewDuplicate(num) {
      return this.genExistingNumbers.has(num)
    },

    addGenPreviewToTable() {
      const existing = new Set(this.fixedPays.filter(r => r.number).map(r => r.number))
      const newNumbers = this.genPreview.filter(n => !existing.has(n))

      if (newNumbers.length === 0) {
        this.toastWarning(this.$t('เลขทั้งหมดมีอยู่ในตารางแล้ว'))
        return
      }

      // ลบแถวเปล่าสุดท้าย (ที่ addNewFixedPayRow สร้างไว้)
      const lastRow = this.fixedPays[this.fixedPays.length - 1]
      if (lastRow && !lastRow.id && !lastRow.number) {
        this.fixedPays.pop()
      }

      const addedTempIds = []
      newNumbers.forEach(num => {
        const pkgRates = {}
        this.ratePackages.forEach(pkg => {
          pkgRates[pkg.id] = this.genPkgRates[pkg.id] != null ? this.genPkgRates[pkg.id] : null
        })
        const tempId = `new-${Date.now()}-${num}`
        addedTempIds.push(tempId)
        this.fixedPays.push({
          _tempId: tempId,
          number: num,
          payRate: this.genPayRate,
          pkgRates,
        })
      })

      // เก็บ action ลง undo stack
      this.genUndoStack.push({ tempIds: addedTempIds })

      // เพิ่มแถวเปล่าท้ายสุด
      this.addNewFixedPayRow()

      const skipped = this.genPreview.length - newNumbers.length
      if (skipped > 0) {
        this.toastSuccess(this.$t('เพิ่ม {added} เลข (ข้าม {skipped} เลขที่ซ้ำ)', {
          added: newNumbers.length,
          skipped,
        }))
      } else {
        this.toastSuccess(this.$t('เพิ่ม {count} เลข', { count: newNumbers.length }))
      }

      this.genPreview = []
      this.genInput = ''
    },

    undoLastGenAdd() {
      const action = this.genUndoStack.pop()
      if (!action) return
      const idsToRemove = new Set(action.tempIds)
      // eslint-disable-next-line no-underscore-dangle
      const removed = this.fixedPays.filter(row => idsToRemove.has(row._tempId))
      const hasContent = removed.some(row => row.number) || removed.length > 1
      // eslint-disable-next-line no-underscore-dangle
      this.fixedPays = this.fixedPays.filter(row => !idsToRemove.has(row._tempId))
      if (hasContent) {
        this.toastSuccess(this.$t('ย้อนกลับ {count} เลข', { count: removed.length }))
      }
    },

    // Tab 4: Load downline caps
    async loadDownlineCaps() {
      this.isLoadingDownline = true
      try {
        let url = `api/riskcap/downline/${this.agent}`
        if (this.selectedHuay) {
          url += `?huayId=${this.selectedHuay.id}`
        }
        const res = await lottoService.getAuthen(url)
        if (res.status === 200) {
          this.downlineCaps = res.data.downlineAgents || []
        }
      } finally {
        this.isLoadingDownline = false
      }
    },

    // Helpers
    getBetTypeName(value) {
      if (value === 'default' || value === -1 || value === '-1') return 'ทุกประเภท'
      const opt = this.betTypeOptions.find(o => o.value === parseInt(value, 10))
      return opt?.label || value
    },

    formatCapAmount(value) {
      if (value === -1) return 'ไม่จำกัด'
      if (value === 0) return '0'
      return value.toLocaleString()
    },

    // Tab 3: ดึงค่า cap สำหรับ downline agent ตาม betType
    getDownlineCapValue(agent, betType) {
      if (!agent.caps || agent.caps.length === 0) return 'ไม่จำกัด'

      const huayId = this.selectedHuay?.id

      // หา cap ที่ match กับ huayId และ betType (priority: specific huay > global)
      let cap = agent.caps.find(c => c.huayId === huayId && c.betType === betType && c.number == null)

      // ถ้าไม่เจอ ลองหา global (huayId = null)
      if (!cap) {
        cap = agent.caps.find(c => c.huayId == null && c.betType === betType && c.number == null)
      }

      // ถ้าไม่เจอ ลองหา "ทุกประเภท" (-1)
      if (!cap) {
        cap = agent.caps.find(c => c.huayId === huayId && c.betType === -1 && c.number == null)
      }

      if (!cap) {
        cap = agent.caps.find(c => c.huayId == null && c.betType === -1 && c.number == null)
      }

      if (!cap) return 'ไม่จำกัด'

      return this.formatCapAmount(cap.capAmount)
    },

    // Tab 3: ตรวจสอบว่าเป็นค่าไม่จำกัดหรือไม่
    isUnlimitedCap(agent, betType) {
      if (!agent.caps || agent.caps.length === 0) return true

      const huayId = this.selectedHuay?.id
      let cap = agent.caps.find(c => c.huayId === huayId && c.betType === betType && c.number == null)

      if (!cap) {
        cap = agent.caps.find(c => c.huayId == null && c.betType === betType && c.number == null)
      }
      if (!cap) {
        cap = agent.caps.find(c => c.huayId === huayId && c.betType === -1 && c.number == null)
      }
      if (!cap) {
        cap = agent.caps.find(c => c.huayId == null && c.betType === -1 && c.number == null)
      }

      if (!cap) return true
      return cap.capAmount === -1
    },
  },
}
</script>

<style scoped>
::v-deep .nav-tabs {
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

::v-deep .nav-tabs .nav-item {
  white-space: nowrap;
}
</style>

<style lang="scss">
@import '@core/scss/base/bootstrap-extended/include';

[dir] .compact-table th,
[dir] .compact-table td {
  padding: 0.72rem 0.75rem !important;
}

/* Group title */
.group-title {
  color: #5d555a;
  font-weight: 600;
  padding: 0.5rem 0;
  border-bottom: 2px solid $primary;
  margin-bottom: 1rem;
}

/* Table border rounded */
.risk-cap-table {
  border: 1px solid #eee4e7;
  border-radius: 0.428rem;
  overflow: hidden;
}

.dark-layout .risk-cap-table {
  border-color: #3b4253;
}

/* Fixed pay loading overlay */
.fixed-pay-table-shell {
  border-radius: 0.428rem;
}

.fixed-pay-table-shell--loading {
  min-height: 10rem;
  background: #f9f7f7;
}

.fixed-pay-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 0.428rem;
}

.dark-layout .fixed-pay-overlay {
  background: rgba(40, 48, 70, 0.5);
}

.fixed-pay-overlay--opaque {
  background: #f9f7f7;
}

.dark-layout .fixed-pay-overlay--opaque {
  background: #161d31;
  border: 1px solid #3b4253;
}

.dark-layout .fixed-pay-table-shell--loading {
  background: #161d31;
}

/* Table header */
.risk-cap-table thead.bg-primary th {
  font-size: 0.85rem;
  font-weight: 600;
  vertical-align: middle;
}

/* Fill all row */
.risk-cap-table .fill-all-row {
  background-color: #f9f7f7;
}

.risk-cap-table .fill-all-row td {
  border-bottom: 2px solid #ddd;
}

/* Huay name cell */
.risk-cap-table .huay-name-cell {
  font-weight: 500;
  background-color: #fafafa;
}

/* Dark mode overrides */
.dark-layout .group-title {
  color: #b4b7bd;
}

.dark-layout .risk-cap-table .fill-all-row {
  background-color: rgba($primary, 0.08);
}

.dark-layout .risk-cap-table .fill-all-row td {
  border-bottom-color: rgba($primary, 0.3);
}

.dark-layout .risk-cap-table .huay-name-cell {
  background-color: transparent;
}

.dark-layout .risk-cap-table .form-control {
  background-color: #283046;
  border-color: #5a6078;
  color: #d0d2d6;
}

.dark-layout .risk-cap-table .form-control:focus {
  border-color: $primary;
  box-shadow: 0 3px 10px 0 rgba(34, 41, 47, 0.1);
}

.dark-layout .risk-cap-table .form-control::placeholder {
  color: #7a7e90 !important;
}

.dark-layout .risk-cap-table td {
  background-color: #283046;
}

/* Row saving state - opacity + disable interaction */
.risk-cap-table .row-saving {
  opacity: 0.5;
  pointer-events: none;
}

/* Unlimited cap - same as input placeholder */
.cap-unlimited {
  font-size: 0.8rem;
  font-weight: 400;
  color: #a8a8b3;
}

.dark-layout .cap-unlimited {
  color: #7a7e90 !important;
}

/* Cap cell */
.risk-cap-table .cap-cell {
  text-align: center;
}

.risk-cap-table .original-value {
  color: #999;
  font-size: 0.75rem;
  display: block;
  margin-bottom: 2px;
}

.dark-layout .risk-cap-table .original-value {
  color: #8a8ea0;
}

/* Input styling */
.risk-cap-table .form-control {
  transition: none;
}

.risk-cap-table input[type="number"] {
  text-align: center;
  padding: 0.25rem 0.5rem;
}

.risk-cap-table input[type="number"]::-webkit-inner-spin-button,
.risk-cap-table input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.risk-cap-table input[type="number"] {
  -moz-appearance: textfield;
}

/* Changed indicator */
.risk-cap-table input.is-changed {
  border-color: #ff9f43 !important;
  background-color: #fff8e7 !important;
}

.dark-layout .risk-cap-table input.is-changed {
  background-color: #3d3522 !important;
  color: #ffcf5c !important;
}

/* New row indicator (สีฟ้า) */
.risk-cap-table input.is-new {
  border-color: #00cfe8 !important;
  background-color: #e8fafd !important;
}

.dark-layout .risk-cap-table input.is-new {
  background-color: #1b3a40 !important;
  color: #6edff6 !important;
}

.risk-cap-table th {
  font-size: 0.85rem;
  font-weight: 600;
  vertical-align: middle;
}

.risk-cap-table td {
  vertical-align: middle;
}

.risk-cap-table .form-control-sm {
  height: calc(1.5em + 0.5rem + 2px);
  font-size: 0.875rem;
}

/* Cap cell td background */
.risk-cap-table .cap-cell-td {
  background-color: #fff;
}

/* Agent link styling */
.agent-link {
  text-decoration: none !important;
  cursor: pointer;
}

.agent-link:hover {
  text-decoration: underline !important;
  color: darken($primary, 6%) !important;
}

/* Dark mode - เปลี่ยนสีเฉพาะตอน hover */
.dark-layout .agent-link:hover {
  color: lighten($primary, 18%) !important;
  text-decoration: underline !important;
}

/* Gen helper panel */
.gen-helper-panel {
  border: 1px solid #eee4e7;
  border-radius: 0.428rem;
  padding: 0.75rem 1rem;
  background: #fafafa;
}

.dark-layout .gen-helper-panel {
  border-color: #3b4253;
  background: #283046;
}

.gen-helper-toggle {
  cursor: pointer;
  user-select: none;
  color: #5d555a;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.gen-helper-toggle:hover {
  color: $primary;
}

.dark-layout .gen-helper-toggle {
  color: #b4b7bd;
}

.dark-layout .gen-helper-toggle:hover {
  color: lighten($primary, 18%);
}

.gen-helper-body {
  margin-top: 0.75rem;
}

/* Gen field (label + input pair) */
.gen-field {
  display: flex;
  flex-direction: column;
}

.gen-field-label {
  font-size: 0.75rem;
  color: #999;
  margin-bottom: 0.2rem;
}

.dark-layout .gen-field-label {
  color: #7a7e90;
}

/* Gen preview numbers */
.gen-preview-numbers {
  max-height: 140px;
  overflow-y: auto;
  line-height: 1.8;
}
</style>
